// This is version 3 of the UserAIChatUI component

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { useVoiceInput } from './VoiceInput';
import profileImage from '../../assets/profile.png';
import AIimage from '../../assets/female_avatar.png';
import { FaPlay, FaPause, FaMicrophone, FaMicrophoneSlash, FaSpinner, FaVolumeUp, FaVolumeMute, FaSyncAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { fetchAndReturnPCM16 } from './AvatarUtils';
import { SimliClient } from 'simli-client';

const simliClient = new SimliClient();

const UserAIChatUI = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [messagePairs, setMessagePairs] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [showButtons, setShowButtons] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isBotResponding, setIsBotResponding] = useState(false);
  const [isChatDisabled, setIsChatDisabled] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [coreService, setCoreService] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [uniqueSellingProposition, setUniqueSellingProposition] = useState('');
  const [problemSolved, setProblemSolved] = useState('');
  const [keyBenefits, setKeyBenefits] = useState('');
  const [primaryGoalOfOutreachCampaign, setPrimaryGoalOfOutreachCampaign] = useState('');
  const [idealClient, setIdealClient] = useState('');
  const [successMeasurement, setSuccessMeasurement]  = useState('');
  const [location, setLocation] = useState('');
  const [industry, setIndustry] = useState('');
  const [suggest_usp, setSuggestUSP] = useState('');
  const [fullResponse, setFullResponse] = useState('');
  const messagesEndRef = useRef(null);
  const [showSummary, setShowSummary] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [intentStatus, setIntentStatus] = useState(null);
  const [awaitingIntentStatus, setAwaitingIntentStatus] = useState(false);
  const [isAvatarVideoReady, setAvatarVideoReady] = useState(false);

  const audioRef = useRef(null);
  const ttsAudioRef = useRef(null);
  const videoRef = useRef(null);

  const messageBubbleStyle = {
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    width: 'fit-content',
  };

  // voice mode
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAvatarPlaying, setAvatarPlaying] = useState(false);
  const { startListening, stopListening, getTranscript,  isListening, browserSupportsSpeechRecognition, resetTranscript } = useVoiceInput();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioContext = useRef(null);
  const [startWebRTC, setStartWebRTC] = useState(false);
  const [isConnected, setConnected] = useState(false)
  const [isWaitingScreen, setWaitingScreen] = useState(true)

  const roboman_api = process.env.REACT_APP_ROBOMAN_API;

  // State to manage mute/unmute
  const [isMuted, setIsMuted] = useState(false);
  const [isPause, setIsPause] = useState(false);
  const [isVoiceInputMode, setVoiceInputMode] = useState(true)

  // Start voice mode when the component mounts
  useEffect(() => {
    if (isWaitingScreen) handleVoiceMode();
  }, [isWaitingScreen]);

  // Debug
  useEffect(() => {
    console.log('State Update:',
      { isListening, isPlaying, showButtons, isVoiceMode, isLoading, startWebRTC, isPause, isVoiceInputMode, isConnected }
    );
  }, [isListening, isPlaying, showButtons, isVoiceMode, audioUrl, isPause, isVoiceInputMode]);

  // Keep fallback TTS audio in sync for non-avatar playback.
  useEffect(() => {
    if (!isVoiceMode && ttsAudioRef.current && audioUrl) {
      ttsAudioRef.current.src = audioUrl;
      ttsAudioRef.current.currentTime = 0;
      ttsAudioRef.current.play().catch(() => {});
    }
  }, [audioUrl, isVoiceMode]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatList = (list) => {
    if (Array.isArray(list)) return list.map((item) => ` ${item}.`).join('\n');
    return list;
  };

  const normalizeIntentStatus = (value) => {
    if (typeof value === 'boolean') return value;
    if (value === null || value === undefined || value === '') return null;

    const normalizedValue = String(value).trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 'on'].includes(normalizedValue)) return true;
    if (['false', '0', 'no', 'n', 'off'].includes(normalizedValue)) return false;

    return null;
  };

  const persistIntentStatus = (value) => {
    const normalizedValue = value ? 'true' : 'false';
    setIntentStatus(value);
    setAwaitingIntentStatus(false);
    localStorage.setItem('buyingIntent', normalizedValue);
    localStorage.setItem('buying_intent', normalizedValue);
    localStorage.setItem('intent_status', normalizedValue);
  };

  const getMessageResponse = async (inputMessage) => {
    try {
      const response = await axios({
        method: 'post',
        url: `${roboman_api}/conversation/message`,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
        },
        data: {
          conversation_id: localStorage.getItem('conversation_id'),
          message_content: inputMessage,
          model_id: 1,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching message response:', error);
    }
  };

  // On mount: load any stored context + greeting
  useEffect(() => {
    const storedCampaignName = localStorage.getItem('campaign_name') || '';
    const storedCoreService = localStorage.getItem('core_service') || '';
    const storedTargetAudience = localStorage.getItem('target_audience') || '';
    const storedUniqueSellingProposition = localStorage.getItem('unique_selling_proposition') || '';
    const storedProblemSolved = localStorage.getItem('problem_solved') || '';
    const storedKeyBenefits = JSON.parse(localStorage.getItem('key_benefits')) || '';
    const storedPrimaryGoalOfOutreachCampaign = localStorage.getItem('primary_goal_of_outreach_campaign') || '';
    const storedIdealClient = JSON.parse(localStorage.getItem('ideal_client')) || '';
    const storedSuccessMeasurement = localStorage.getItem('success_measurement') || '';
    const storedIntentStatus = normalizeIntentStatus(
      localStorage.getItem('buyingIntent') ??
      localStorage.getItem('buying_intent') ??
      localStorage.getItem('intent_status')
    );

    setCampaignName(storedCampaignName);
    setCoreService(storedCoreService);
    setTargetAudience(storedTargetAudience);
    setUniqueSellingProposition(storedUniqueSellingProposition);
    setProblemSolved(storedProblemSolved);
    setKeyBenefits(storedKeyBenefits);
    setPrimaryGoalOfOutreachCampaign(storedPrimaryGoalOfOutreachCampaign);
    setIdealClient(storedIdealClient);
    setSuccessMeasurement(storedSuccessMeasurement);
    setIntentStatus(storedIntentStatus);

    const greeting_prompt = `This is a started message from user. Username is ${localStorage.getItem('nickname')}. Please return a greeting message in the happiness tone, no icon for respect, asking for her situation and provide help. For example: Hi ${localStorage.getItem('nickname')}, how are you today? I hope you always feel fine and happy. How can I help you today?.`;

    if (
      storedCampaignName && storedCoreService && storedTargetAudience &&
      storedUniqueSellingProposition && storedProblemSolved && storedKeyBenefits &&
      storedPrimaryGoalOfOutreachCampaign && storedIdealClient && storedSuccessMeasurement
    ) {
      const fetchhistory = async () => {
        const hisdata = await updatechathistory();
        const chathis = historydatatochat(hisdata);
        setMessages(chathis);
      };
      fetchhistory();
      console.log('All field present');
    } else if (localStorage.getItem('conversation_id') === null) {
      stopListening();
      const fetchGreetingText = async () => {
        await getConversationId();
        const greeting_text = await getMessageResponse(greeting_prompt);
        setMessages([{ sender: 'bot', text: greeting_text }]);
        if (greeting_text) {
          await sum_and_speech(greeting_text, false, true);
        }
        simulateStreaming(greeting_text);
      };
      fetchGreetingText();
    } else {
      const fetchhistory = async () => {
        const hisdata = await updatechathistory();
        const chathis = historydatatochat(hisdata);
        setMessages(chathis);
      };
      fetchhistory();
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const checkAllFieldsFilled = useCallback(() => {
    return campaignName && coreService && targetAudience && uniqueSellingProposition && problemSolved && keyBenefits && primaryGoalOfOutreachCampaign && idealClient && successMeasurement && intentStatus !== null;
  }, [campaignName, coreService, targetAudience, uniqueSellingProposition, problemSolved, keyBenefits, primaryGoalOfOutreachCampaign, idealClient, successMeasurement, intentStatus]);

  useEffect(() => {
    if (checkAllFieldsFilled() && showSummary) {
      setShowButtons(true);
      setIsChatDisabled(true);
      const welcomeMessage = `Here are your summary information: 
            **Campaign Name**: ${campaignName}
            **Core Service/Product**: ${coreService}
            **Target Audience**: ${targetAudience}
            **Unique Selling Proposition**: ${uniqueSellingProposition}
            **Problem Solved**: ${problemSolved}
            **Key Benefits**: ${formatList(keyBenefits)}
            **Primary Goal of Outreach Campaign**: ${primaryGoalOfOutreachCampaign}
            **Ideal Client**: ${formatList(idealClient)}
            **Success Measurement**: ${successMeasurement}
            **Intent Status**: ${intentStatus ? 'true' : 'false'}`;
      setMessages(prev => [...prev, { sender: 'bot', text: welcomeMessage }]);
    } else {
      setShowButtons(false);
      setIsChatDisabled(false);
    }
  }, [checkAllFieldsFilled, showSummary, campaignName, coreService, targetAudience, uniqueSellingProposition, problemSolved, keyBenefits, primaryGoalOfOutreachCampaign, idealClient, successMeasurement, intentStatus]);

  const hasAllFields = (message) => {
    const requiredFields = [
      'Campaign Name',
      'Service',
      'Product',
      'Audience',
      'Unique Selling Proposition',
      'Problem Solved',
      'Benefits',
      'Primary Goal',
      'Main Objective',
      'Ideal Client',
      'Success Measurement',
      'Success Metric',
      'Intent Status',
      'Buying Intent',
    ];
    const threshold = 7;
    let matchCount = 0;

    for (let field of requiredFields) {
      const regex = new RegExp(field.replace(/[-/]/g, '.?'), 'i');
      if (regex.test(message)) matchCount++;
    }

    return matchCount >= threshold;
  };

  useEffect(() => {
    if (!isStreaming && fullResponse) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.sender === 'bot' && hasAllFields(lastMessage.text)) {
        extractCampaignInfo(lastMessage.text).then(async () => {
          setFullResponse('');
          stopListening();

          const storedIntentStatus = normalizeIntentStatus(
            localStorage.getItem('buyingIntent') ??
            localStorage.getItem('buying_intent') ??
            localStorage.getItem('intent_status')
          );

          if (storedIntentStatus === null) {
            if (!awaitingIntentStatus) {
              const intentPrompt = 'One last thing before we continue: should `intent_Status` be enabled? Please answer Yes or No.';
              setAwaitingIntentStatus(true);
              setShowButtons(false);
              setIsChatDisabled(false);
              setShowSummary(false);
              setMessages(prev => [...prev, { sender: 'bot', text: intentPrompt }]);
              if (isVoiceMode) {
                await sum_and_speech(intentPrompt, false, true);
              }
            }
          } else {
            setIntentStatus(storedIntentStatus);
            setShowButtons(true);
            setIsChatDisabled(true);
            setShowSummary(true);
          }
        });
      } else {
        setFullResponse('');
      }
    }
  }, [isStreaming, fullResponse, messages, messagePairs, awaitingIntentStatus, isVoiceMode]);

  const getConversationId = async () => {
    try {
      const response = await fetch(`${roboman_api}/conversation/new`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) console.error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (data.conversation_id) {
        localStorage.setItem('conversation_id', data.conversation_id);
      }
    } catch (error) {
      console.error('Error getting conversation ID:', error);
    }
  };

  const extractCampaignInfo = async (text) => {
    try {
      const response = await fetch(`${roboman_api}/llm/campaign/parser`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
        },
        body: JSON.stringify({ input_str: text }),
      });

      if (!response.ok) console.error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      if (data) {
        const parsedIntentStatus = normalizeIntentStatus(
          data.intent_Status ?? data.intent_status ?? data.buying_intent ?? data.buyingIntent
        );

        localStorage.setItem('campaign_name', data.campaign_name);
        localStorage.setItem('core_service', data.core_service);
        localStorage.setItem('unique_selling_proposition', data.unique_selling_proposition);
        localStorage.setItem('target_audience', data.target_audience);
        localStorage.setItem('problem_solved', data.problem_solved);
        localStorage.setItem('key_benefits', JSON.stringify(data.key_benefits));
        localStorage.setItem('primary_goal_of_outreach_campaign', data.primary_goal_of_outreach_campaign);
        localStorage.setItem('ideal_client', JSON.stringify(data.ideal_client));
        localStorage.setItem('success_measurement', data.success_measurement);
        localStorage.setItem('suggest_usp', data.suggest_unique_selling_point);

        if (parsedIntentStatus !== null) {
          const normalizedValue = parsedIntentStatus ? 'true' : 'false';
          localStorage.setItem('buyingIntent', normalizedValue);
          localStorage.setItem('buying_intent', normalizedValue);
          localStorage.setItem('intent_status', normalizedValue);
        }

        return new Promise((resolve) => {
          setCampaignName(data.campaign_name);
          setCoreService(data.core_service);
          setTargetAudience(data.target_audience);
          setUniqueSellingProposition(data.unique_selling_proposition);
          setProblemSolved(data.problem_solved);
          setKeyBenefits(data.key_benefits);
          setPrimaryGoalOfOutreachCampaign(data.primary_goal_of_outreach_campaign);
          setIdealClient(data.ideal_client);
          setSuccessMeasurement(data.success_measurement);
          setSuggestUSP(data.suggest_unique_selling_point);
          if (parsedIntentStatus !== null) setIntentStatus(parsedIntentStatus);
          resolve();
        });
      }
    } catch (error) {
      console.error('Error extracting campaign info:', error);
    }
  };

  const updatechathistory = async () => {
    const convid = localStorage.getItem('conversation_id');
    try {
      const response = await fetch(`${roboman_api}/conversation/${convid}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
        },
      });

      if (!response.ok) console.error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      return data;
    } catch (error) {
      setError(error.message);
    }
  };

  const historydatatochat = (data) => {
    const history = data.flatMap((item, index) => {
      const userMessage = item.message_data.user;
      const botMessage = item.message_data.assistant;

      const messages = [];
      if (index !== 0) {
        messages.push({ sender: 'user', text: userMessage });
      }
      messages.push({ sender: 'bot', text: botMessage });

      return messages;
    });

    return history;
  };

  const sum_and_speech = async (assistant, is_summary = true, isAvatarMode = false) => {
    try {
      const response = await fetch(`${roboman_api}/openai/text-to-speech`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text_input: assistant,
          is_summary: is_summary,
        }),
      });

      if (!response.ok) console.error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      setAudioUrl(result.image_url);

      const playFallbackAudio = async (shouldRestoreAfterPlayback = false) => {
        if (!ttsAudioRef.current || !result.image_url) return false;

        try {
          ttsAudioRef.current.pause();
          ttsAudioRef.current.src = result.image_url;
          ttsAudioRef.current.currentTime = 0;

          if (shouldRestoreAfterPlayback) {
            stopListening();
            setAvatarPlaying(true);
            ttsAudioRef.current.onended = () => {
              ttsAudioRef.current.onended = null;
              restoreMicAfterSpeech();
            };
          } else {
            ttsAudioRef.current.onended = null;
          }

          await ttsAudioRef.current.play();
          return true;
        } catch (_) {
          if (shouldRestoreAfterPlayback) restoreMicAfterSpeech();
          return false;
        }
      };

      if (!isAvatarMode) {
        await playFallbackAudio(false);
      }

      if (isAvatarMode) {
        const canUseAvatarStream = isConnected && startWebRTC;

        if (!canUseAvatarStream) {
          await playFallbackAudio(true);
          return;
        }

        const audio8uintdata = await fetchAndReturnPCM16(result.image_url);

        if (!audio8uintdata || !audio8uintdata.length) {
          await playFallbackAudio(true);
          return;
        }

        clearAvatarSpeechFallback();
        stopListening();
        setAvatarPlaying(true);

        const estimatedDurationMs = Math.max(
          2500,
          Math.ceil((audio8uintdata.length / 16000) * 1000) + 1200
        );

        avatarSpeechTimeoutRef.current = setTimeout(() => {
          restoreMicAfterSpeech();
        }, estimatedDurationMs);

        const chunkSize = 6000;
        for (let i = 0; i < audio8uintdata.length; i += chunkSize) {
          const chunk = audio8uintdata.slice(i, i + chunkSize);
          simliClient.sendAudioData(chunk);
        }
      }
    } catch (error) {
      setError(error.message);
      restoreMicAfterSpeech();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (voiceMode = false) => {
    const summary = !voiceMode;
    if (inputMessage.trim() === '' || isBotResponding) return;

    if (awaitingIntentStatus) {
      const parsedIntentStatus = normalizeIntentStatus(inputMessage);
      const userReply = inputMessage;

      setMessages(prev => [...prev, { sender: 'user', text: userReply }]);
      resetTranscript();
      setInputMessage('');

      if (parsedIntentStatus === null) {
        const retryMessage = 'Please answer `Yes` or `No` for `intent_Status`.';
        setMessages(prev => [...prev, { sender: 'bot', text: retryMessage }]);
        if (isVoiceMode) await sum_and_speech(retryMessage, false, true);
        return;
      }

      persistIntentStatus(parsedIntentStatus);
      const confirmationMessage = `Thanks — \`intent_Status\` has been set to ${parsedIntentStatus ? 'true' : 'false'}.`;
      setMessages(prev => [...prev, { sender: 'bot', text: confirmationMessage }]);
      if (isVoiceMode) await sum_and_speech(confirmationMessage, false, true);
      setShowSummary(true);
      setIsChatDisabled(true);
      stopListening();
      return;
    }

    if (isListening) stopListening();

    setIsBotResponding(true);
    setMessages(prev => [...prev, { sender: 'user', text: inputMessage }]);
    resetTranscript();
    setInputMessage('');
    setShowButtons(false);
    setIsStreaming(true);
    setFullResponse('');

    setMessages(prev => [...prev, { sender: 'bot', text: 'Loading...' }]);

    try {
      let fullResponse = '';
      const response = await axios({
        method: 'post',
        url: `${roboman_api}/conversation/message`,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': localStorage.getItem('access_token'),
        },
        data: {
          conversation_id: localStorage.getItem('conversation_id'),
          message_content: inputMessage,
          model_id: 1,
        },
      });
      fullResponse = response.data;
      setFullResponse(fullResponse);

      const check = hasAllFields(fullResponse);
      if (check) {
        simulateStreaming(fullResponse);
      } else {
        await sum_and_speech(fullResponse, summary, isVoiceMode);
        simulateStreaming(fullResponse);
      }
    } catch (error) {
      console.error('Error:', error);
      let errorMessage = 'Sorry, I encountered an error. Please try again.';
      if (error.response) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.request) {
        errorMessage = 'No response from the server. Please check your internet connection.';
      }
      setMessages(prev => [...prev, { sender: 'bot', text: errorMessage }]);
    } finally {
      setIsBotResponding(false);
    }
  };

  const simulateStreaming = (fullText) => {
    if (!fullText || typeof fullText !== 'string') {
      console.error('simulateStreaming received invalid input:', fullText);
      return;
    }

    const words = fullText.split(' ');
    let currentText = '';

    words.forEach((word, index) => {
      setTimeout(() => {
        currentText += `${word} `;
        setMessages(prev => {
          const updated = [...prev];
          if (updated[updated.length - 1]?.sender === 'bot') {
            updated[updated.length - 1].text = currentText.trim();
          } else {
            updated.push({ sender: 'bot', text: currentText.trim() });
          }
          return updated;
        });
      }, index * 100);
    });

    setTimeout(() => {
      setIsStreaming(false);
    }, words.length * 100);
  };

  const handleConfirm = () => {
    setShowButtons(false);
    setShowSummary(false);
    navigate('/reviewinfo');
    simliClient.close();
  };

  const handleAddDetails = () => {
    setShowButtons(false);
    setIsChatDisabled(false);
    setShowSummary(false);
  };

  const handleAddDetailsChat = () => {
    setShowButtons(false);
    setIsChatDisabled(false);
    setShowSummary(false);
    if (isVoiceInputMode) startListening();
  };

  const handleVoiceInput = (voiceMode = false) => {
    if (isListening) {
      stopListening();
      resetTranscript();
      if (voiceMode === true) handleSendMessage(true);
    } else {
      startListening();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) return;
      e.preventDefault();
      handleSendMessage();
    }
  };

  const [transcriptTimeout, setTranscriptTimeout] = useState(null);
  const [previousTranscript, setPreviousTranscript] = useState('');
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  /** ---------------- Voice Mode: entry point ---------------- */
  const handleVoiceMode = () => {
    resetTranscript();
    setIsVoiceMode(true);
    setWaitingScreen(true);
  };
  /** --------------------------------------------------------- */

  const handleCloseVoiceMode = () => {
    stopListening();
    simliClient.close();
    setStartWebRTC(false);
    navigate('/dashboard');
  };

  const [messageSent, setMessageSent] = useState(false);
  const transcriptTimeoutRef = useRef(null);

  const resetAutoSendTimeout = () => {
    if (transcriptTimeoutRef.current) clearTimeout(transcriptTimeoutRef.current);
    transcriptTimeoutRef.current = setTimeout(async () => {
      setTranscribedText('');
      if (inputMessage && inputMessage.trim() !== '' && isVoiceMode && !messageSent) {
        try {
          await handleSendMessage(true);
          setMessageSent(true);
        } catch (error) {
          console.error('Error in handleSendMessage:', error);
        }
      }
    }, 2500);
  };

  useEffect(() => {
    const transcript = getTranscript();
    if (isVoiceMode && isListening) {
      if (transcript !== inputMessage) {
        setInputMessage(transcript);
        setTranscribedText(transcript);
        setMessageSent(false);
      } else if (transcript === inputMessage && !messageSent) {
        resetAutoSendTimeout();
      }
    } else {
      if (transcript) setInputMessage(transcript);
    }
  }, [getTranscript, isListening, isVoiceMode, inputMessage, messageSent]);

  /** --- SIMLI INIT (per docs) --------------------------------------------- */
  const initializeSimliClient = useCallback(() => {
    const v = videoRef.current;
    const a = audioRef.current;

    if (!v || !a) {
      console.warn('Simli init: media elements not ready yet');
      return false;
    }

    if (!process.env.REACT_APP_SIMLI_API_KEY || !process.env.REACT_APP_SIMLI_FACEID_FEMALE) {
      console.error('Simli init: missing REACT_APP_SIMLI_API_KEY or REACT_APP_SIMLI_FACEID_FEMALE');
    }

    simliClient.Initialize({
      apiKey: process.env.REACT_APP_SIMLI_API_KEY,
      faceID: process.env.REACT_APP_SIMLI_FACEID_FEMALE,
      handleSilence: true,
      maxSessionLength: 1800,
      maxIdleTime: 120,
      videoRef: v,                 // DOM nodes (not refs)
      audioRef: a,
      enableConsoleLogs: true,
    });

    console.log('Simli Client initialized (with DOM nodes)');
    return true;
  }, []);

  // Try init every 100ms until refs exist, then start the client
  useEffect(() => {
    if (!isVoiceMode) return;

    let tries = 0;
    const id = setInterval(async () => {
      tries++;

      if (initializeSimliClient()) {
        clearInterval(id);
        try {
          await simliClient.start();
          setStartWebRTC(true);

          setTimeout(() => {
            try {
              const audioData = new Uint8Array(1000).fill(0);
              simliClient.sendAudioData(audioData);
            } catch (warmupError) {
              console.error('Simli warm-up error:', warmupError);
            }
          }, 1500);
        } catch (e) {
          console.error('Simli start error:', e);
        }
      } else if (tries > 50) {
        clearInterval(id);
        console.error('Simli init failed: media elements never became available.');
      }
    }, 100);

    return () => clearInterval(id);
  }, [isVoiceMode, initializeSimliClient]);
  /** ----------------------------------------------------------------------- */

  const voiceInputModeRef = useRef(isVoiceInputMode);
  const showButtonsRef = useRef(showButtons);
  const isPauseRef = useRef(isPause);
  const avatarSpeechTimeoutRef = useRef(null);

  const clearAvatarSpeechFallback = useCallback(() => {
    if (avatarSpeechTimeoutRef.current) {
      clearTimeout(avatarSpeechTimeoutRef.current);
      avatarSpeechTimeoutRef.current = null;
    }
  }, []);

  const restoreMicAfterSpeech = useCallback(() => {
    clearAvatarSpeechFallback();
    setAvatarPlaying(false);
    setWaitingScreen(false);

    if (showButtonsRef.current) {
      stopListening();
      return;
    }

    if (voiceInputModeRef.current && !isPauseRef.current) {
      startListening();
    } else {
      stopListening();
    }
  }, [clearAvatarSpeechFallback, startListening, stopListening]);

  useEffect(() => {
    voiceInputModeRef.current = isVoiceInputMode;
    showButtonsRef.current = showButtons;
    isPauseRef.current = isPause;
  }, [isVoiceInputMode, showButtons, isPause]);

  useEffect(() => {
    simliClient.on('connected', () => {
      setIsLoading(false);
      setConnected(true);
      setWaitingScreen(false);
      console.log('SimliClient is now connected!');
      if (isVoiceMode && isVoiceInputMode) startListening();
    });

    simliClient.on('disconnected', () => {
      setStartWebRTC(false);
      setConnected(false);
      setAvatarVideoReady(false);
      setWaitingScreen(false);
      console.log('SimliClient has disconnected!');
    });

    simliClient.on('failed', () => {
      setStartWebRTC(false);
      setConnected(false);
      setAvatarVideoReady(false);
      setWaitingScreen(false);
      setVoiceInputMode(false);
      console.log('SimliClient has failed to connect!');
    });

    simliClient.on('speaking', () => {
      clearAvatarSpeechFallback();
      stopListening();
      setAvatarPlaying(true);
      console.log('Avatar is speaking');
    });

    simliClient.on('silent', () => {
      console.log('Avatar is silent');
      restoreMicAfterSpeech();
    });

    return () => {
      clearAvatarSpeechFallback();
    };
  }, [clearAvatarSpeechFallback, restoreMicAfterSpeech, startListening, stopListening]);

  const handleMuteToggle = () => {
    if (videoRef.current || audioRef.current) {
      if (videoRef.current) {
        videoRef.current.muted = !isMuted;
        videoRef.current.volume = !isMuted ? 0 : 1.0;
      }
      if (audioRef.current) {
        audioRef.current.muted = !isMuted;
        audioRef.current.volume = !isMuted ? 0 : 1.0;
      }
      setIsMuted(!isMuted);
    } else {
      console.warn('Audio or video reference is not available.');
    }
  };

  const handlePause = () => stopListening();
  const handleResume = () => startListening();

  const handleToggleMode = () => {
    if (isVoiceInputMode) {
      stopListening();
    } else {
      startListening();
    }
    setIsPause(false);
    setVoiceInputMode(!isVoiceInputMode);
  };

  const handleReload = () => window.location.reload();

  return (
    <div className="min-h-screen overflow-auto bg-[#f5f5f7] font-['SF_Pro_Display','SF_Pro_Text','Inter','Segoe_UI',sans-serif] text-slate-800 antialiased">

      {/* Voice Mode Overlay */}
      {isVoiceMode && (
        <div className="fixed inset-0 z-50 overflow-auto bg-[#f5f5f7] text-slate-800">
          <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col px-4 py-5 md:px-6 lg:px-8">
            <div className="mb-5 flex items-center justify-center">
              <div className="inline-flex items-center rounded-full border border-[#dbe7ff] bg-white px-5 py-2 text-[11px] font-medium tracking-[0.2em] text-[#2563eb] shadow-[0_4px_20px_rgba(37,99,235,0.06)]">
                HELP US CUSTOMIZE YOUR EXPERIENCE WITH OUR ASSISTANT
              </div>
            </div>

            <div className="grid flex-1 grid-cols-1 gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
              {/* Avatar Panel */}
              <div className="rounded-[30px] border border-[#dbe7ff] bg-white p-4 shadow-[0_12px_34px_rgba(37,99,235,0.06)] transition-all duration-300 lg:p-5">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400">AI guide</p>
                    <h2 className="mt-1 text-[22px] font-semibold tracking-[-0.02em] text-slate-900">Anna</h2>
                    <p className="mt-1 text-sm text-slate-500">Your onboarding assistant</p>
                  </div>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold ${
                      isConnected
                        ? 'bg-[#eff6ff] text-[#2563eb]'
                        : 'bg-[#eff6ff] text-[#3b82f6]'
                    }`}
                  >
                    {isConnected ? 'Live' : 'Connecting'}
                  </span>
                </div>

                <div className="relative mx-auto flex aspect-square w-full max-w-[300px] items-center justify-center overflow-hidden rounded-[32px] bg-[linear-gradient(180deg,#f8fbff_0%,#eef4ff_100%)] shadow-inner ring-1 ring-[#dbe7ff] transition-all duration-500">
                  {(!isConnected || !isAvatarVideoReady) && (
                    <img
                      src={AIimage}
                      alt="AI avatar placeholder"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  )}
                  <video
                    ref={videoRef}
                    id="simli_video"
                    autoPlay
                    playsInline
                    muted
                    onLoadedData={() => setAvatarVideoReady(true)}
                    onEmptied={() => setAvatarVideoReady(false)}
                    className="h-full w-full object-cover"
                  />
                  <audio
                    ref={audioRef}
                    id="simli_audio"
                    autoPlay
                  />
                  <audio
                    ref={ttsAudioRef}
                    id="tts_audio"
                    autoPlay
                    className="hidden"
                  />
                </div>

                <div className="mt-4 space-y-3">
                  <p className="text-sm leading-6 text-slate-600">
                    Share your campaign details naturally. Anna will guide the setup and prepare everything for review.
                  </p>

                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-[#eff6ff] px-3 py-1 text-xs font-medium text-[#2563eb] transition-all duration-200">
                      {isVoiceInputMode ? 'Voice-first' : 'Text-first'}
                    </span>
                    <span className="rounded-full bg-[#eff6ff] px-3 py-1 text-xs font-medium text-[#2563eb] transition-all duration-200">
                      {isAvatarPlaying ? 'Speaking now' : 'Ready to assist'}
                    </span>
                  </div>

                  <button
                    onClick={handleReload}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-[20px] bg-[#2563eb] px-4 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(37,99,235,0.20)] transition-all duration-200 hover:-translate-y-[1px] hover:bg-[#1d4ed8]"
                  >
                    <FaSyncAlt size={16} />
                    Reload session
                  </button>
                </div>
              </div>

              {/* Chat Panel */}
              <div className="flex flex-col overflow-hidden rounded-[30px] border border-[#dbe7ff] bg-white shadow-[0_12px_34px_rgba(37,99,235,0.06)] transition-all duration-300">
                <div className="border-b border-slate-200/70 px-4 py-4 sm:px-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Conversation</p>
                      <h1 className="mt-1 text-[22px] font-semibold tracking-[-0.02em] text-slate-900">Campaign onboarding</h1>
                      <p className="mt-1 text-sm text-slate-500">
                        A calm, focused setup for your outreach campaign.
                      </p>
                    </div>
                    <span
                      className={`hidden sm:inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                        isBotResponding
                          ? 'bg-[#f2f2f7] text-slate-600'
                          : 'bg-[#f2f2f7] text-slate-600'
                      }`}
                    >
                      {isBotResponding ? 'Thinking…' : isAvatarPlaying ? 'Speaking…' : 'Ready'}
                    </span>
                  </div>
                </div>

                <div className="flex-1 px-4 py-4 sm:px-5">
                  <div className="h-[320px] rounded-[26px] border border-[#e5edff] bg-[#fbfcff] p-3 shadow-inner md:h-[500px]">
                    <div className="h-full overflow-y-auto pr-1 scroll-smooth">
                      {messages.map((message, index) => (
                        <div
                          key={index}
                          className={`mb-4 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`flex max-w-[95%] items-end gap-3 ${message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                            <div
                              className={`h-10 w-10 overflow-hidden rounded-2xl ring-1 ring-slate-200 ${
                                message.sender === 'user' ? 'bg-slate-100' : 'bg-indigo-100'
                              }`}
                            >
                              <img
                                src={message.sender === 'user' ? profileImage : AIimage}
                                alt={message.sender === 'user' ? 'User Avatar' : 'AI Avatar'}
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <div
                              className={`rounded-2xl px-4 py-3 text-sm leading-6 shadow-sm ${
                                message.sender === 'user'
                                  ? 'bg-[#2563eb] text-white shadow-[0_8px_20px_rgba(37,99,235,0.18)] transition-all duration-200'
                                  : 'border border-[#e5edff] bg-white text-slate-700 transition-all duration-200'
                              } max-w-[90%] md:max-w-[820px]`}
                              style={messageBubbleStyle}
                            >
                              {message.sender === 'user' ? (
                                message.text
                              ) : (
                                <ReactMarkdown>{message.text}</ReactMarkdown>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200/70 bg-white/70 px-4 py-4 sm:px-5">
                  {isVoiceInputMode ? (
                    <div className="rounded-[24px] border border-[#e5edff] bg-[#f8fbff] px-4 py-5 text-center shadow-inner transition-all duration-300">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                        Live status
                      </p>
                      <p className="mt-2 text-sm font-medium text-slate-700">
                        {!startWebRTC
                          ? 'Waiting for Anna to connect'
                          : isAvatarPlaying
                          ? 'Anna is speaking'
                          : (isListening && !transcribedText)
                          ? 'Listening…'
                          : transcribedText || 'Waiting for your response…'}
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-end gap-3 rounded-[26px] border border-[#dbe7ff] bg-[#f8fbff] px-3 py-3 shadow-inner transition-all duration-300 focus-within:border-[#93c5fd] focus-within:shadow-[0_0_0_4px_rgba(59,130,246,0.10)]">
                      <textarea
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e)}
                        className="min-h-[88px] flex-1 resize-none bg-transparent px-3 py-2 text-[15px] leading-6 text-slate-700 outline-none placeholder:text-slate-400"
                        placeholder="Type your message..."
                        disabled={isBotResponding || isChatDisabled || showButtons}
                        rows={3}
                      />
                      <button
                        onClick={() => handleSendMessage(isVoiceMode)}
                        className={`mb-1 inline-flex h-11 w-11 items-center justify-center rounded-full text-white shadow-md transition ${
                          isBotResponding || isChatDisabled
                            ? 'bg-slate-300'
                            : 'bg-[#2563eb] hover:bg-[#1d4ed8]'
                        }`}
                        disabled={isBotResponding || isChatDisabled || showButtons}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </button>
                    </div>
                  )}

                  <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
                    <button
                      onClick={() => handleToggleMode()}
                      className={`inline-flex min-w-[170px] items-center justify-center rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                        isBotResponding || isChatDisabled || showButtons
                          ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                          : 'border border-[#cfe0ff] bg-white text-[#2563eb] hover:-translate-y-[1px] hover:bg-[#f8fbff]'
                      }`}
                      disabled={isBotResponding || isChatDisabled || showButtons}
                    >
                      {isVoiceInputMode ? 'Switch to Text' : 'Switch to Voice'}
                    </button>

                    <div className="text-xs text-slate-500">
                      Designed to feel simple, calm, and focused
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-center">
              {!showButtons && (
                <div className="flex flex-wrap items-center justify-center gap-3 rounded-full border border-[#dbe7ff] bg-white px-3 py-2 shadow-[0_8px_24px_rgba(37,99,235,0.08)] transition-all duration-300">
                  <button
                    onClick={() => handleVoiceInput(true)}
                    className={`rounded-full p-3.5 text-white transition-all duration-200 ${isListening && isVoiceInputMode ? 'bg-[#2563eb] scale-[1.02]' : 'bg-slate-500 hover:bg-slate-600'}`}
                    disabled={isAvatarPlaying || showButtons}
                  >
                    {isListening && isVoiceInputMode ? <FaMicrophone size={20} /> : <FaMicrophoneSlash size={20} />}
                  </button>

                  <button
                    onClick={() => {
                      if (isPause) handleResume();
                      else handlePause();
                      setIsPause(!isPause);
                    }}
                    disabled={!isVoiceInputMode}
                    className={`rounded-full p-3.5 transition ${
                      !isVoiceInputMode ? 'cursor-not-allowed bg-slate-200 text-slate-400' : isPause ? 'bg-[#60a5fa] text-white' : 'bg-[#2563eb] text-white hover:bg-[#1d4ed8]'
                    }`}
                  >
                    {!isPause ? <FaPause size={20} /> : <FaPlay size={20} />}
                  </button>

                  <button
                    onClick={handleMuteToggle}
                    className={`rounded-full p-3.5 text-white transition-all duration-200 ${isMuted ? 'bg-slate-500' : 'bg-[#2563eb] hover:bg-[#1d4ed8]'}`}
                  >
                    {isMuted ? <FaVolumeMute size={20} /> : <FaVolumeUp size={20} />}
                  </button>

                  <button
                    onClick={handleCloseVoiceMode}
                    className="rounded-full bg-[#2563eb] p-3.5 text-white transition-all duration-200 hover:bg-[#1d4ed8]"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6m0 12L6 6" />
                    </svg>
                  </button>
                </div>
              )}

              {showButtons && (
                <div className="flex flex-wrap items-center justify-center gap-3 rounded-full border border-[#dbe7ff] bg-white px-3 py-2 shadow-[0_8px_24px_rgba(37,99,235,0.08)] transition-all duration-300">
                  <button
                    onClick={handleConfirm}
                    className="rounded-full bg-[#2563eb] px-6 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(37,99,235,0.18)] transition-all duration-200 hover:-translate-y-[1px] hover:bg-[#1d4ed8]"
                  >
                    Confirm and Proceed
                  </button>
                  <button
                    onClick={handleAddDetailsChat}
                    className="rounded-full border border-[#cfe0ff] bg-white px-6 py-2.5 text-sm font-semibold text-[#2563eb] shadow-sm transition-all duration-200 hover:-translate-y-[1px] hover:bg-[#f8fbff]"
                  >
                    {isVoiceInputMode ? 'Continue Speaking' : 'Continue Chatting'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Waiting Screen Overlay */}
      {isWaitingScreen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
          <div className="rounded-[28px] border border-[#dbe7ff] bg-white px-8 py-8 text-center shadow-[0_10px_35px_rgba(37,99,235,0.08)]">
            <div className="flex justify-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#f2f2f7]">
                <FaSpinner className="animate-spin text-5xl text-[#2563eb]" />
              </div>
            </div>
            <p className="mt-5 text-lg font-semibold text-slate-900">Anna is preparing your workspace…</p>
            <p className="mt-2 text-sm text-slate-500">Please wait a moment while the assistant gets ready.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAIChatUI;
